/**
 * Base Theme
 * 
 * Default theme that all brand variants extend from
 */

import { HSLColor } from '../tokens/colors';

export interface ThemeColors {
  background: HSLColor;
  foreground: HSLColor;
  primary: HSLColor;
  'primary-foreground': HSLColor;
  secondary: HSLColor;
  'secondary-foreground': HSLColor;
  accent: HSLColor;
  'accent-foreground': HSLColor;
  muted: HSLColor;
  'muted-foreground': HSLColor;
  destructive: HSLColor;
  'destructive-foreground': HSLColor;
  border: HSLColor;
  input: HSLColor;
  ring: HSLColor;
  card: HSLColor;
  'card-foreground': HSLColor;
  popover: HSLColor;
  'popover-foreground': HSLColor;
}

export interface Theme {
  name: string;
  description: string;
  light: ThemeColors;
  dark: ThemeColors;
  // Brand-specific overrides
  fontFamily?: {
    display?: string;
    sans?: string;
    serif?: string;
    mono?: string;
  };
  // Animation preferences
  animations?: {
    reducedMotion?: boolean;
    duration?: 'fast' | 'base' | 'slow';
  };
  // Shadow intensity
  shadows?: {
    intensity?: 'subtle' | 'moderate' | 'strong';
  };
}

/**
 * Base theme (current default)
 */
export const baseTheme: Theme = {
  name: 'Base',
  description: 'Default balanced theme',
  light: {
    background: { h: 0, s: 0, l: 100 },
    foreground: { h: 222, s: 84, l: 5 },
    primary: { h: 221, s: 83, l: 53 },
    'primary-foreground': { h: 210, s: 40, l: 98 },
    secondary: { h: 210, s: 40, l: 96 },
    'secondary-foreground': { h: 222, s: 47, l: 11 },
    accent: { h: 210, s: 40, l: 96 },
    'accent-foreground': { h: 222, s: 47, l: 11 },
    muted: { h: 210, s: 40, l: 96 },
    'muted-foreground': { h: 215, s: 16, l: 47 },
    destructive: { h: 0, s: 84, l: 60 },
    'destructive-foreground': { h: 210, s: 40, l: 98 },
    border: { h: 214, s: 32, l: 91 },
    input: { h: 214, s: 32, l: 91 },
    ring: { h: 221, s: 83, l: 53 },
    card: { h: 0, s: 0, l: 100 },
    'card-foreground': { h: 222, s: 84, l: 5 },
    popover: { h: 0, s: 0, l: 100 },
    'popover-foreground': { h: 222, s: 84, l: 5 },
  },
  dark: {
    background: { h: 222, s: 84, l: 5 },
    foreground: { h: 210, s: 40, l: 98 },
    primary: { h: 217, s: 91, l: 60 },
    'primary-foreground': { h: 222, s: 47, l: 11 },
    secondary: { h: 217, s: 33, l: 17 },
    'secondary-foreground': { h: 210, s: 40, l: 98 },
    accent: { h: 217, s: 33, l: 17 },
    'accent-foreground': { h: 210, s: 40, l: 98 },
    muted: { h: 217, s: 33, l: 17 },
    'muted-foreground': { h: 215, s: 20, l: 65 },
    destructive: { h: 0, s: 63, l: 31 },
    'destructive-foreground': { h: 210, s: 40, l: 98 },
    border: { h: 217, s: 33, l: 17 },
    input: { h: 217, s: 33, l: 17 },
    ring: { h: 224, s: 77, l: 76 },
    card: { h: 222, s: 84, l: 5 },
    'card-foreground': { h: 210, s: 40, l: 98 },
    popover: { h: 222, s: 84, l: 5 },
    'popover-foreground': { h: 210, s: 40, l: 98 },
  },
};
